<template>
  <section class="closed-trades" :class="{ 'closed-trades--dark': $vuetify.theme.dark }">
    <div class="closed-trades__toolbar">
      <v-text-field
        v-model="search"
        class="closed-trades__control closed-trades__search"
        prepend-inner-icon="mdi-magnify"
        append-icon="mdi-crosshairs-gps"
        placeholder="Search by ticket, symbol, or acc. no..."
        solo
        flat
        dense
        hide-details
        clearable
      />

      <v-select
        v-model="account"
        :items="accountOptions"
        class="closed-trades__control"
        append-icon="mdi-chevron-down"
        solo
        flat
        dense
        hide-details
      />

      <v-dialog ref="dateDialog" v-model="modal" :return-value.sync="dates" width="310px">
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            :value="dateRangeText"
            class="closed-trades__control closed-trades__date"
            prepend-inner-icon="mdi-calendar-blank-outline"
            append-icon="mdi-chevron-down"
            readonly
            solo
            flat
            dense
            hide-details
            v-bind="attrs"
            v-on="on"
          />
        </template>
        <v-date-picker v-model="dates" scrollable range>
          <v-spacer />
          <v-btn text color="#ff4d5f" @click="clearDates">Reset</v-btn>
          <v-btn text color="primary" @click="$refs.dateDialog.save(dates)">Apply</v-btn>
        </v-date-picker>
      </v-dialog>

      <div class="closed-trades__totals">
        <div v-for="stat in topStats" :key="stat.label" class="closed-trades__total">
          <span>{{ stat.label }}</span>
          <strong :class="`is-${stat.tone}`">{{ stat.value }}</strong>
        </div>
      </div>
    </div>

    <v-card flat class="closed-trades__table-card">
      <div class="closed-trades__table-actions">
        <v-btn text small class="closed-trades__export" :disabled="!filteredData.length" @click="exportCsv">
          <v-icon left size="17">mdi-download-outline</v-icon>
          Export
        </v-btn>
      </div>

      <v-data-table
        :headers="headers"
        :items="filteredData"
        :search="search"
        :items-per-page="options.itemsPerPage"
        :options.sync="options"
        :server-items-length="serverItemsLength"
        :loading="loading"
        :footer-props="footerProps"
        class="closed-trades__table"
        loading-text="Loading closed trades..."
        no-data-text="No closed trades found"
      >
        <template v-slot:[`item.serial`]="{ index }">
          <span class="closed-trades__serial">{{ serialNumber(index) }}</span>
        </template>

        <template v-slot:[`item.Time`]="{ item }">
          <span class="closed-trades__datetime">{{ formatDateTime(item.Time) }}</span>
        </template>

        <template v-slot:[`item.trading_account_no`]="{ item }">
          <span class="closed-trades__account">{{ item.trading_account_no || '—' }}</span>
        </template>

        <template v-slot:[`item.Symbol`]="{ item }">
          <span class="closed-trades__symbol">
            <span class="closed-trades__symbol-icon" :class="symbolClass(item.Symbol)">{{ symbolInitial(item.Symbol) }}</span>
            {{ item.Symbol || '—' }}
          </span>
        </template>

        <template v-slot:[`item.Action`]="{ item }">
          <span class="closed-trades__side" :class="isBuy(item) ? 'is-buy' : 'is-sell'">
            {{ tradeSide(item) }}
          </span>
        </template>

        <template v-slot:[`item.Volume`]="{ item }">{{ formatNumber(lotValue(item), 2) }}</template>
        <template v-slot:[`item.Price`]="{ item }">{{ formatNumber(item.Price, 5) }}</template>
        <template v-slot:[`item.PricePosition`]="{ item }">{{ formatNumber(item.PricePosition, 5) }}</template>

        <template v-slot:[`item.PriceSL`]="{ item }">
          <span>{{ slTpValue(item) }}</span>
        </template>

        <template v-slot:[`item.Storage`]="{ item }">
          <span :class="valueTone(item.Storage)">{{ formatNumber(item.Storage, 2) }}</span>
        </template>

        <template v-slot:[`item.Commission`]="{ item }">
          <span :class="valueTone(commissionValue(item))">{{ formatNumber(commissionValue(item), 2) }}</span>
        </template>

        <template v-slot:[`item.Profit`]="{ item }">
          <strong :class="valueTone(item.Profit)">{{ formatCurrency(item.Profit) }}</strong>
        </template>
      </v-data-table>
    </v-card>

    <div class="closed-trades__analytics">
      <v-card flat class="analytics-card performance-card">
        <h3><v-icon size="17">mdi-chart-histogram</v-icon>Performance Summary</h3>
        <div class="performance-grid">
          <div v-for="metric in performanceMetrics" :key="metric.label" class="performance-metric">
            <span>{{ metric.label }}</span>
            <strong :class="`is-${metric.tone}`">{{ metric.value }}</strong>
          </div>
        </div>
      </v-card>

      <v-card flat class="analytics-card line-card">
        <h3>P/L Over Time</h3>
        <div class="pl-line-chart">
          <svg viewBox="0 0 360 140" preserveAspectRatio="none" role="img" aria-label="Profit and loss over time">
            <line v-for="line in [20, 55, 90, 125]" :key="line" x1="0" :y1="line" x2="360" :y2="line" class="chart-grid-line" />
            <line x1="0" :y1="lineZeroY" x2="360" :y2="lineZeroY" class="chart-zero-line" />
            <polyline :points="lineSvgPoints" class="pl-line" />
            <circle v-for="point in lineSvgCoordinates" :key="point.key" :cx="point.x" :cy="point.y" r="3" class="pl-point" />
          </svg>
          <div class="chart-x-labels"><span v-for="item in lineChartData" :key="item.Time">{{ shortDate(item.Time) }}</span></div>
        </div>
      </v-card>

      <v-card flat class="analytics-card distribution-card">
        <h3><v-icon size="17">mdi-chart-donut</v-icon>P/L Distribution</h3>
        <div class="distribution-content">
          <div class="distribution-chart">
            <div class="distribution-ring" :style="distributionRingStyle"></div>
            <div class="distribution-total">
              <span>Total P/L</span>
              <strong :class="valueTone(totalProfit)">{{ formatCompactCurrency(totalProfit) }}</strong>
            </div>
          </div>
          <div class="distribution-legend">
            <div>
              <i class="profit-dot" />
              <span>Profit Trades</span>
              <strong>{{ formatCurrency(profitTradeValue) }} ({{ profitPercentage }}%)</strong>
            </div>
            <div>
              <i class="loss-dot" />
              <span>Loss Trades</span>
              <strong>{{ formatCurrency(lossTradeValue) }} ({{ lossPercentage }}%)</strong>
            </div>
          </div>
        </div>
      </v-card>

      <v-card flat class="analytics-card monthly-card">
        <div class="analytics-card__heading">
          <h3>Monthly Overview</h3>
          <span class="analytics-pill">P/L</span>
        </div>
        <div class="monthly-chart">
          <div class="monthly-grid"><i></i><i></i><i></i><i></i><span></span></div>
          <div class="monthly-bars">
            <div v-for="item in monthlyBars" :key="item.label" class="monthly-bar-item">
              <div class="monthly-bar-area">
                <i v-if="item.value >= 0" class="monthly-bar is-positive" :style="{ height: item.height + '%' }"></i>
                <i v-else class="monthly-bar is-negative" :style="{ height: item.height + '%' }"></i>
              </div>
              <small>{{ item.label }}</small>
            </div>
          </div>
        </div>
      </v-card>
    </div>
  </section>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { TRADING__ACTIONS } from "@/store/action-types";

export default {
  name: "ClosedTradesTab",

  data() {
    return {
      modal: false,
      search: "",
      account: "All Accounts",
      dates: [],
      loading: false,
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: [],
      },
      footerProps: {
        "items-per-page-options": [5, 10, 15, 25],
        "items-per-page-text": "Rows per page:",
        "show-current-page": true,
        "show-first-last-page": false,
      },
      headers: [
        { text: "Sl", value: "serial", sortable: false, width: 48 },
        { text: "Date & Time", value: "Time", width: 154 },
        { text: "Acc. No.", value: "trading_account_no", width: 112 },
        { text: "Ticket", value: "PositionID", width: 104 },
        { text: "Symbol", value: "Symbol", width: 106 },
        { text: "Type", value: "Action", width: 72 },
        { text: "Lots", value: "Volume", width: 68 },
        { text: "Open Price", value: "Price", width: 100 },
        { text: "Close Price", value: "PricePosition", width: 100 },
        { text: "SL-TP", value: "PriceSL", sortable: false, width: 145 },
        { text: "Swap", value: "Storage", width: 76 },
        { text: "Commission", value: "Commission", width: 94 },
        { text: "Profit", value: "Profit", width: 104 },
      ],
    };
  },

  computed: {
    ...mapState("trading", ["allCloseTrades", "allCloseTradesPagination", "accountSummary"]),

    sourceTrades() {
      return Array.isArray(this.allCloseTrades) ? this.allCloseTrades : [];
    },

    filteredData() {
      let rows = this.sourceTrades;

      if (this.account && this.account !== "All Accounts") {
        rows = rows.filter((item) => String(item.trading_account_no) === String(this.account));
      }

      if (this.dates.length === 2) {
        const boundaries = this.dates.slice().sort();
        const start = new Date(`${boundaries[0]}T00:00:00`);
        const end = new Date(`${boundaries[1]}T23:59:59`);
        rows = rows.filter((item) => {
          const tradeDate = this.parseDate(item.Time);
          return tradeDate && tradeDate >= start && tradeDate <= end;
        });
      }

      return rows;
    },

    serverItemsLength() {
      if (this.account !== "All Accounts" || this.dates.length === 2) return this.filteredData.length;
      return Number(this.allCloseTradesPagination?.total || this.filteredData.length);
    },

    accountOptions() {
      const accounts = this.sourceTrades
        .map((item) => item.trading_account_no)
        .filter((value) => value !== null && value !== undefined);
      return ["All Accounts", ...new Set(accounts)];
    },

    dateRangeText() {
      if (this.dates.length === 2) return this.dates.slice().sort().join(" - ");
      const from = this.allCloseTradesPagination?.last7DaysFrom;
      return [from ? String(from).slice(0, 10) : this.getSevenDaysAgoDate(), this.getCurrentDate()].join(" - ");
    },

    summaryRows() {
      return this.filteredData;
    },

    totalProfit() {
      if (this.account === "All Accounts" && this.dates.length !== 2) {
        const apiTotal = this.accountSummary?.closedTrades?.profitLoss;
        if (apiTotal !== null && apiTotal !== undefined) return this.numberValue(apiTotal);
      }
      return this.sumBy(this.summaryRows, "Profit");
    },

    totalLots() {
      if (this.account === "All Accounts" && this.dates.length !== 2) {
        const apiTotal = this.accountSummary?.closedTrades?.totalLot;
        if (apiTotal !== null && apiTotal !== undefined) return this.numberValue(apiTotal);
      }
      return this.summaryRows.reduce((sum, item) => sum + this.lotValue(item), 0);
    },

    totalEquity() {
      const apiTotal = this.accountSummary?.closedTrades?.equity;
      return apiTotal !== null && apiTotal !== undefined
        ? this.numberValue(apiTotal)
        : this.sumBy(this.summaryRows, "Equity");
    },

    totalSwap() {
      return this.sumBy(this.summaryRows, "Storage");
    },

    totalCommission() {
      return this.summaryRows.reduce((sum, item) => sum + this.commissionValue(item), 0);
    },

    topStats() {
      return [
        { label: "Total Equity", value: this.formatCurrency(this.totalEquity), tone: this.totalEquity >= 0 ? "profit" : "loss" },
        { label: "Total Closed P/L", value: this.formatCurrency(this.totalProfit), tone: this.totalProfit >= 0 ? "profit" : "loss" },
        { label: "Total Lots", value: this.formatNumber(this.totalLots, 2), tone: "neutral" },
        { label: "Total Commission", value: this.formatCurrency(this.totalCommission), tone: this.totalCommission > 0 ? "warning" : "loss" },
        { label: "Total Swaps", value: this.formatCurrency(this.totalSwap), tone: this.totalSwap >= 0 ? "profit" : "loss" },
      ];
    },

    winningTrades() {
      return this.summaryRows.filter((item) => this.numberValue(item.Profit) > 0);
    },

    losingTrades() {
      return this.summaryRows.filter((item) => this.numberValue(item.Profit) < 0);
    },

    bestTrade() {
      return this.summaryRows.length
        ? Math.max(...this.summaryRows.map((item) => this.numberValue(item.Profit)))
        : 0;
    },

    worstTrade() {
      return this.summaryRows.length
        ? Math.min(...this.summaryRows.map((item) => this.numberValue(item.Profit)))
        : 0;
    },

    averageProfit() {
      return this.summaryRows.length ? this.sumBy(this.summaryRows, "Profit") / this.summaryRows.length : 0;
    },

    performanceMetrics() {
      const count = this.summaryRows.length;
      const percentage = (value) => count ? ((value / count) * 100).toFixed(1) : "0.0";
      return [
        { label: "Total Trades", value: this.serverItemsLength, tone: "primary" },
        { label: "Winning Trades", value: `${this.winningTrades.length} (${percentage(this.winningTrades.length)}%)`, tone: "profit" },
        { label: "Losing Trades", value: `${this.losingTrades.length} (${percentage(this.losingTrades.length)}%)`, tone: "loss" },
        { label: "Best Trade", value: this.formatCurrency(this.bestTrade), tone: "profit" },
        { label: "Worst Trade", value: this.formatCurrency(this.worstTrade), tone: "loss" },
        { label: "Avg. P/L", value: this.formatCurrency(this.averageProfit), tone: this.averageProfit >= 0 ? "profit" : "loss" },
      ];
    },

    chronologicalTrades() {
      return [...this.summaryRows].sort((a, b) => {
        const first = this.parseDate(a.Time);
        const second = this.parseDate(b.Time);
        return (first?.getTime() || 0) - (second?.getTime() || 0);
      });
    },

    chartTextColor() {
      return this.$vuetify.theme.dark ? "#8f9ab3" : "#707a98";
    },

    chartGridColor() {
      return this.$vuetify.theme.dark ? "#1a2a41" : "#e9edf5";
    },

    lineSeries() {
      let runningTotal = 0;
      return [{
        name: "P/L",
        data: this.lineChartData.map((item) => {
          runningTotal += this.numberValue(item.Profit);
          return Number(runningTotal.toFixed(2));
        }),
      }];
    },

    lineChartData() {
      if (this.chronologicalTrades.length) return this.chronologicalTrades;
      return Array.from({ length: 7 }, (_, index) => {
        const date = new Date();
        date.setDate(date.getDate() - (6 - index));
        return { Time: date.toISOString(), Profit: 0 };
      });
    },

    lineSvgCoordinates() {
      const values = this.lineSeries[0].data;
      const minimum = Math.min(0, ...values);
      const maximum = Math.max(0, ...values);
      const isFlat = maximum === minimum;
      const range = maximum - minimum || 2;
      return values.map((value, index) => ({
        key: `${index}-${value}`,
        x: values.length > 1 ? (index / (values.length - 1)) * 360 : 180,
        y: isFlat ? 70 : 130 - ((value - minimum) / range) * 120,
      }));
    },

    lineSvgPoints() {
      return this.lineSvgCoordinates.map(point => `${point.x},${point.y}`).join(" ");
    },

    lineZeroY() {
      const values = this.lineSeries[0].data;
      const minimum = Math.min(0, ...values);
      const maximum = Math.max(0, ...values);
      if (maximum === minimum) return 70;
      const range = maximum - minimum || 2;
      return 130 - ((0 - minimum) / range) * 120;
    },

    lineChartOptions() {
      return {
        chart: { toolbar: { show: false }, zoom: { enabled: false }, background: "transparent" },
        colors: ["#e3a600"],
        stroke: { curve: "straight", width: 2 },
        markers: { size: 2.5, strokeWidth: 0 },
        dataLabels: { enabled: false },
        grid: { borderColor: this.chartGridColor, strokeDashArray: 0, padding: { left: 4, right: 8, top: -8, bottom: -8 } },
        xaxis: {
          categories: this.lineChartData.map((item) => this.shortDate(item.Time)),
          labels: { style: { colors: this.chartTextColor, fontSize: "10px" }, rotate: 0, hideOverlappingLabels: true },
          axisBorder: { show: false }, axisTicks: { show: false }, tooltip: { enabled: false },
        },
        yaxis: { labels: { style: { colors: [this.chartTextColor], fontSize: "10px" }, formatter: this.compactNumber }, tickAmount: 3 },
        tooltip: { theme: this.$vuetify.theme.dark ? "dark" : "light", y: { formatter: this.formatCurrency } },
      };
    },

    profitTradeValue() {
      return this.winningTrades.reduce((sum, item) => sum + this.numberValue(item.Profit), 0);
    },

    lossTradeValue() {
      return Math.abs(this.losingTrades.reduce((sum, item) => sum + this.numberValue(item.Profit), 0));
    },

    distributionTotal() {
      return this.profitTradeValue + this.lossTradeValue;
    },

    profitPercentage() {
      return this.distributionTotal ? ((this.profitTradeValue / this.distributionTotal) * 100).toFixed(1) : "0.0";
    },

    lossPercentage() {
      return this.distributionTotal ? ((this.lossTradeValue / this.distributionTotal) * 100).toFixed(1) : "0.0";
    },

    donutSeries() {
      if (!this.distributionTotal) return [1];
      return [this.profitTradeValue, this.lossTradeValue];
    },

    distributionRingStyle() {
      if (!this.distributionTotal) {
        return { background: `conic-gradient(${this.chartGridColor} 0deg 360deg)` };
      }
      const profitDegrees = (this.profitTradeValue / this.distributionTotal) * 360;
      return {
        background: `conic-gradient(#11b978 0deg ${profitDegrees}deg, #f23d4f ${profitDegrees}deg 360deg)`,
      };
    },

    donutChartOptions() {
      return {
        chart: { background: "transparent" },
        labels: this.distributionTotal ? ["Profit Trades", "Loss Trades"] : ["No trades"],
        colors: this.distributionTotal ? ["#11b978", "#f23d4f"] : [this.chartGridColor],
        dataLabels: { enabled: false },
        legend: { show: false },
        stroke: { width: 0 },
        plotOptions: { pie: { donut: { size: "70%", labels: { show: false } } } },
        tooltip: { enabled: Boolean(this.distributionTotal), theme: this.$vuetify.theme.dark ? "dark" : "light", y: { formatter: this.formatCurrency } },
      };
    },

    monthlyData() {
      const groups = new Map();
      this.chronologicalTrades.forEach((item) => {
        const date = this.parseDate(item.Time);
        if (!date) return;
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
        const label = date.toLocaleDateString("en-US", { month: "short" });
        const current = groups.get(key) || { label, value: 0 };
        current.value += this.numberValue(item.Profit);
        groups.set(key, current);
      });
      const values = [...groups.values()].slice(-8);
      if (values.length) return values;
      return Array.from({ length: 8 }, (_, index) => {
        const date = new Date();
        date.setDate(1);
        date.setMonth(date.getMonth() - (7 - index));
        return { label: date.toLocaleDateString("en-US", { month: "short" }), value: 0 };
      });
    },

    monthlySeries() {
      return [{ name: "P/L", data: this.monthlyData.map((item) => Number(item.value.toFixed(2))) }];
    },

    monthlyBars() {
      const largest = Math.max(1, ...this.monthlyData.map(item => Math.abs(item.value)));
      return this.monthlyData.map(item => ({
        ...item,
        height: item.value === 0 ? 2 : Math.max(8, (Math.abs(item.value) / largest) * 46),
      }));
    },

    monthlyChartOptions() {
      return {
        chart: { toolbar: { show: false }, background: "transparent" },
        colors: ["#13b978"],
        plotOptions: { bar: { columnWidth: "48%", borderRadius: 0, colors: { ranges: [{ from: -Infinity, to: -0.001, color: "#ef4051" }] } } },
        dataLabels: { enabled: false },
        grid: { borderColor: this.chartGridColor, padding: { left: 4, right: 4, top: -8, bottom: -8 } },
        xaxis: {
          categories: this.monthlyData.map((item) => item.label),
          labels: { style: { colors: this.chartTextColor, fontSize: "10px" } },
          axisBorder: { show: false }, axisTicks: { show: false },
        },
        yaxis: { labels: { style: { colors: [this.chartTextColor], fontSize: "10px" }, formatter: this.compactNumber }, tickAmount: 3 },
        tooltip: { theme: this.$vuetify.theme.dark ? "dark" : "light", y: { formatter: this.formatCurrency } },
      };
    },
  },

  watch: {
    "options.page"() {
      this.fetchClosedTrades();
    },
    "options.itemsPerPage"() {
      if (this.options.page !== 1) this.options.page = 1;
      else this.fetchClosedTrades();
    },
  },

  methods: {
    ...mapActions("trading", [TRADING__ACTIONS.ALLTRADEDETAILS]),

    async fetchClosedTrades() {
      this.loading = true;
      try {
        await this[TRADING__ACTIONS.ALLTRADEDETAILS]({
          closed_page: this.options.page || 1,
          closed_per_page: this.options.itemsPerPage > 0 ? this.options.itemsPerPage : 10,
        });
      } finally {
        this.loading = false;
      }
    },

    numberValue(value) {
      const number = Number(value);
      return Number.isFinite(number) ? number : 0;
    },

    sumBy(rows, key) {
      return rows.reduce((sum, item) => sum + this.numberValue(item[key]), 0);
    },

    commissionValue(item) {
      return this.numberValue(item.Commission ?? item.CommissionAgent ?? item.CommissionDaily ?? 0);
    },

    lotValue(item) {
      const volume = this.numberValue(item.VolumeClosed ?? item.Volume);
      return volume / 10000;
    },

    formatNumber(value, digits = 2) {
      return this.numberValue(value).toLocaleString("en-US", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      });
    },

    formatCurrency(value) {
      const number = this.numberValue(value);
      const sign = number < 0 ? "-$" : "$";
      return `${sign}${Math.abs(number).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },

    formatCompactCurrency(value) {
      const number = this.numberValue(value);
      return `${number < 0 ? "-" : ""}$${this.compactNumber(Math.abs(number))}`;
    },

    compactNumber(value) {
      const number = Number(value) || 0;
      if (Math.abs(number) >= 1000000) return `${(number / 1000000).toFixed(1)}M`;
      if (Math.abs(number) >= 1000) return `${(number / 1000).toFixed(0)}K`;
      return number.toFixed(0);
    },

    parseDate(value) {
      if (!value) return null;
      const parsed = new Date(String(value).replace(" ", "T"));
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    },

    formatDateTime(value) {
      const date = this.parseDate(value);
      if (!date) return "—";
      const datePart = date.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
      const timePart = date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
      return `${datePart} ${timePart}`;
    },

    shortDate(value) {
      const date = this.parseDate(value);
      return date ? date.toLocaleDateString("en-US", { day: "2-digit", month: "short" }) : "";
    },

    tradeSide(item) {
      const action = Number(item.Action);
      if (action === 0) return "Buy";
      if (action === 1) return "Sell";
      return item.Type || "—";
    },

    isBuy(item) {
      return this.tradeSide(item).toLowerCase() === "buy";
    },

    slTpValue(item) {
      const stopLoss = this.numberValue(item.PriceSL);
      const takeProfit = this.numberValue(item.PriceTP);
      if (!stopLoss && !takeProfit) return "—";
      return `${stopLoss ? this.formatNumber(stopLoss, 5) : "—"} / ${takeProfit ? this.formatNumber(takeProfit, 5) : "—"}`;
    },

    valueTone(value) {
      const number = this.numberValue(value);
      if (number > 0) return "is-profit";
      if (number < 0) return "is-loss";
      return "is-neutral";
    },

    symbolInitial(symbol) {
      const value = String(symbol || "?").replace(/[^A-Za-z0-9]/g, "");
      return value.charAt(0).toUpperCase() || "?";
    },

    symbolClass(symbol) {
      const value = String(symbol || "").toUpperCase();
      if (value.includes("BTC") || value.includes("XAU")) return "is-gold";
      if (value.includes("GBP") || value.includes("EUR")) return "is-blue";
      return "is-indigo";
    },

    serialNumber(index) {
      const start = Number(this.allCloseTradesPagination?.from || ((this.options.page - 1) * this.options.itemsPerPage + 1));
      return start + index;
    },

    getCurrentDate() {
      return new Date().toISOString().split("T")[0];
    },

    getSevenDaysAgoDate() {
      const date = new Date();
      date.setDate(date.getDate() - 7);
      return date.toISOString().split("T")[0];
    },

    clearDates() {
      this.dates = [];
      this.modal = false;
    },

    exportCsv() {
      const columns = ["Time", "trading_account_no", "PositionID", "Symbol", "Action", "Volume", "Price", "PricePosition", "PriceSL", "PriceTP", "Storage", "Commission", "Profit"];
      const headings = ["Date & Time", "Account No.", "Ticket", "Symbol", "Type", "Lots", "Open Price", "Close Price", "SL", "TP", "Swap", "Commission", "Profit"];
      const escapeCell = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
      const lines = [headings.map(escapeCell).join(",")];

      this.filteredData.forEach((item) => {
        const row = {
          ...item,
          Time: this.formatDateTime(item.Time),
          Action: this.tradeSide(item),
          Volume: this.lotValue(item),
          Commission: this.commissionValue(item),
        };
        lines.push(columns.map((key) => escapeCell(row[key])).join(","));
      });

      const blob = new Blob([`\ufeff${lines.join("\n")}`], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `closed-trades-${this.getCurrentDate()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    },
  },

  mounted() {
    this.fetchClosedTrades();
  },
};
</script>

<style scoped>
.closed-trades {
  --panel: #fff;
  --panel-soft: #fbfcff;
  --field: #fff;
  --row-alt: #f8faff;
  --border: #e1e6f0;
  --text: #111b3c;
  --muted: #717a97;
  --subtle: #94a0b9;
  --primary: #d99a00;
  --profit: #04b96d;
  --loss: #ff394b;
  --warning: #ef8800;
  color: var(--text);
}

.closed-trades--dark {
  --panel: #07111b;
  --panel-soft: #0b182b;
  --field: #08131e;
  --row-alt: #101923;
  --border: #293026;
  --text: #f3f6ff;
  --muted: #aeb6c0;
  --subtle: #71819f;
  --primary: #ffc400;
  --profit: #12df8a;
  --loss: #ff5060;
  --warning: #ff9f16;
}

.closed-trades__toolbar {
  display: grid;
  grid-template-columns: minmax(250px, 1.25fr) minmax(170px, .72fr) minmax(245px, 1fr) minmax(490px, 1.7fr);
  gap: 10px;
  align-items: stretch;
  margin-bottom: 18px;
}

.closed-trades__control {
  min-width: 0;
}

.closed-trades__control ::v-deep .v-input__slot {
  min-height: 48px !important;
  border: 1px solid var(--border);
  border-radius: 8px !important;
  background: var(--field) !important;
  color: var(--text);
  box-shadow: none !important;
}

.closed-trades__control ::v-deep input,
.closed-trades__control ::v-deep .v-select__selection {
  color: var(--text) !important;
  font-size: 12px;
  font-weight: 500;
}

.closed-trades__control ::v-deep .v-icon {
  color: #7584ad !important;
  font-size: 20px;
}

.closed-trades__totals {
  display: grid;
  grid-template-columns: repeat(5, minmax(90px, 1fr));
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--panel);
}

.closed-trades__total {
  display: flex;
  min-width: 0;
  padding: 10px 13px;
  flex-direction: column;
  justify-content: center;
  border-left: 1px solid var(--border);
}

.closed-trades__total:first-child { border-left: 0; }
.closed-trades__total span { margin-bottom: 3px; color: var(--muted); font-size: 10px; white-space: nowrap; }
.closed-trades__total strong { overflow: hidden; font-size: 15px; line-height: 1.2; text-overflow: ellipsis; white-space: nowrap; }

.is-profit { color: var(--profit) !important; }
.is-loss { color: var(--loss) !important; }
.is-warning { color: var(--warning) !important; }
.is-primary { color: var(--primary) !important; }
.is-neutral { color: var(--text) !important; }

.closed-trades__table-card {
  position: relative;
  overflow: hidden;
  margin-bottom: 20px;
  border: 1px solid var(--border);
  border-radius: 9px !important;
  background: var(--panel) !important;
}

.closed-trades__table-actions {
  display: flex;
  height: 40px;
  align-items: center;
  justify-content: flex-end;
  padding: 0 10px;
  border-bottom: 1px solid var(--border);
}

.closed-trades__export {
  color: var(--text) !important;
  font-size: 11px;
  letter-spacing: 0;
  text-transform: none;
}

.closed-trades__table {
  background: transparent !important;
  color: var(--text) !important;
}

.closed-trades__table ::v-deep .v-data-table__wrapper { overflow-x: auto; }
.closed-trades__table ::v-deep table { min-width: 1325px; }
.closed-trades__table ::v-deep th {
  height: 40px;
  padding: 0 10px !important;
  border-color: var(--border) !important;
  background: var(--panel) !important;
  color: var(--muted) !important;
  font-size: 10px !important;
  font-weight: 600 !important;
  white-space: nowrap;
}

.closed-trades__table ::v-deep td {
  height: 43px;
  padding: 0 10px !important;
  border-color: var(--border) !important;
  color: var(--text) !important;
  font-size: 10.5px !important;
  font-weight: 550;
  white-space: nowrap;
}

.closed-trades__table ::v-deep tbody tr:nth-child(odd) { background: var(--row-alt); }
.closed-trades__table ::v-deep tbody tr { border-left: 2px solid var(--profit); }
.closed-trades__table ::v-deep tbody tr:hover { background: rgba(217, 154, 0, .06) !important; }

.closed-trades__table ::v-deep .v-data-footer {
  min-height: 52px;
  border-color: var(--border) !important;
  color: var(--muted);
  font-size: 11px;
}

.closed-trades__table ::v-deep .v-data-footer .v-icon,
.closed-trades__table ::v-deep .v-select__selection { color: var(--text) !important; }
.closed-trades__table ::v-deep .v-progress-linear { color: var(--primary) !important; }

.closed-trades__serial { color: var(--text); }
.closed-trades__datetime { font-variant-numeric: tabular-nums; }
.closed-trades__account {
  display: inline-flex;
  padding: 3px 7px;
  border-radius: 5px;
  background: rgba(31, 131, 255, .12);
  color: #1682ed;
  font-weight: 700;
}

.closed-trades__symbol { display: inline-flex; align-items: center; gap: 7px; }
.closed-trades__symbol-icon {
  display: inline-grid;
  width: 18px;
  height: 18px;
  place-items: center;
  border-radius: 50%;
  background: #d99a00;
  color: #fff;
  font-size: 9px;
  font-weight: 800;
}
.closed-trades__symbol-icon.is-gold { background: #f0a512; }
.closed-trades__symbol-icon.is-blue { background: #176ee8; }
.closed-trades__symbol-icon.is-indigo { background: #3f51b5; }
.closed-trades__side { padding-bottom: 2px; border-bottom: 1px solid currentColor; font-weight: 700; }
.closed-trades__side.is-buy { color: var(--profit); }
.closed-trades__side.is-sell { color: var(--loss); }

.closed-trades__analytics {
  display: grid;
  grid-template-columns: 1.05fr 1.15fr .95fr 1.15fr;
  gap: 12px;
}

.analytics-card {
  min-width: 0;
  min-height: 224px;
  padding: 12px;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 9px !important;
  background: var(--panel) !important;
  color: var(--text) !important;
}

.analytics-card h3 {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 0 0 10px;
  color: var(--text);
  font-size: 12px;
  font-weight: 700;
}

.analytics-card h3 .v-icon { color: var(--primary); }
.analytics-card__heading { display: flex; align-items: center; justify-content: space-between; }
.analytics-pill { padding: 5px 14px; border-radius: 5px; background: var(--primary); color: #fff; font-size: 10px; }

.performance-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.performance-metric {
  display: flex;
  min-width: 0;
  min-height: 76px;
  padding: 10px;
  flex-direction: column;
  justify-content: space-between;
  border: 1px solid var(--border);
  border-radius: 7px;
  background: var(--panel-soft);
}

.performance-metric span { color: var(--muted); font-size: 9.5px; }
.performance-metric strong { overflow: hidden; color: var(--text); font-size: 13px; text-overflow: ellipsis; white-space: nowrap; }

.distribution-content { display: flex; height: 177px; align-items: center; }
.distribution-chart { position: relative; display: flex; height: 170px; flex: 0 0 160px; align-items: center; justify-content: center; }
.distribution-ring {
  position: relative;
  width: 122px;
  height: 122px;
  flex: 0 0 122px;
  border-radius: 50%;
  box-shadow: 0 0 14px rgba(17, 185, 120, .08);
}
.distribution-ring::after {
  position: absolute;
  inset: 25px;
  content: "";
  border-radius: 50%;
  background: var(--panel);
}
.distribution-total {
  position: absolute;
  top: 50%;
  left: 50%;
  display: flex;
  width: 82px;
  flex-direction: column;
  align-items: center;
  transform: translate(-50%, -50%);
  text-align: center;
  pointer-events: none;
}
.distribution-total span { color: var(--muted); font-size: 9px; }
.distribution-total strong { font-size: 11px; }
.distribution-legend { display: flex; min-width: 0; flex: 1; flex-direction: column; gap: 20px; }
.distribution-legend > div { position: relative; display: grid; padding-left: 17px; }
.distribution-legend i { position: absolute; top: 3px; left: 0; width: 9px; height: 9px; border-radius: 50%; }
.distribution-legend .profit-dot { background: var(--profit); }
.distribution-legend .loss-dot { background: var(--loss); }
.distribution-legend span { color: var(--text); font-size: 10px; font-weight: 650; }
.distribution-legend strong { color: var(--text); font-size: 10px; font-weight: 550; }

.pl-line-chart { height: 177px; padding: 4px 4px 0; }
.pl-line-chart svg { display: block; width: 100%; height: 145px; overflow: visible; }
.chart-grid-line { stroke: var(--border); stroke-width: 1; vector-effect: non-scaling-stroke; }
.chart-zero-line { stroke: var(--muted); stroke-width: 1; stroke-dasharray: 3 3; opacity: .55; vector-effect: non-scaling-stroke; }
.pl-line { fill: none; stroke: var(--primary); stroke-width: 2.2; stroke-linecap: round; stroke-linejoin: round; vector-effect: non-scaling-stroke; }
.pl-point { fill: var(--primary); stroke: var(--panel); stroke-width: 1.5; vector-effect: non-scaling-stroke; }
.chart-x-labels { display: flex; justify-content: space-between; gap: 3px; color: var(--muted); font-size: 8px; }
.chart-x-labels span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.monthly-chart { position: relative; height: 177px; padding: 3px 5px 0; }
.monthly-grid { position: absolute; inset: 3px 5px 23px; display: flex; flex-direction: column; justify-content: space-between; pointer-events: none; }
.monthly-grid i { width: 100%; height: 1px; background: var(--border); }
.monthly-grid span { position: absolute; top: 50%; left: 0; width: 100%; height: 1px; background: var(--muted); opacity: .6; }
.monthly-bars { position: relative; z-index: 1; display: flex; height: 100%; align-items: stretch; justify-content: space-around; gap: 5px; }
.monthly-bar-item { display: flex; min-width: 0; flex: 1; flex-direction: column; align-items: center; }
.monthly-bar-area { position: relative; width: 100%; height: 150px; }
.monthly-bar { position: absolute; left: 50%; width: min(22px, 55%); min-height: 2px; transform: translateX(-50%); border-radius: 2px 2px 0 0; }
.monthly-bar.is-positive { bottom: 50%; background: linear-gradient(180deg, #14d890, #08a968); }
.monthly-bar.is-negative { top: 50%; border-radius: 0 0 2px 2px; background: linear-gradient(180deg, #ff6675, #e93549); }
.monthly-bar-item small { height: 20px; color: var(--muted); font-size: 8px; line-height: 18px; }

.analytics-card ::v-deep .apexcharts-canvas,
.analytics-card ::v-deep .apexcharts-svg { background: transparent !important; }

@media (max-width: 1500px) {
  .closed-trades__toolbar { grid-template-columns: minmax(230px, 1fr) minmax(160px, .65fr) minmax(230px, .9fr); }
  .closed-trades__totals { grid-column: 1 / -1; }
  .closed-trades__analytics { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 900px) {
  .closed-trades__toolbar { grid-template-columns: 1fr 1fr; }
  .closed-trades__search, .closed-trades__totals { grid-column: 1 / -1; }
  .closed-trades__analytics { grid-template-columns: 1fr; }
}

@media (max-width: 600px) {
  .closed-trades__toolbar { grid-template-columns: 1fr; }
  .closed-trades__search, .closed-trades__totals { grid-column: auto; }
  .closed-trades__totals { grid-template-columns: repeat(2, 1fr); }
  .closed-trades__total { min-height: 62px; border-top: 1px solid var(--border); }
  .closed-trades__total:nth-child(odd) { border-left: 0; }
  .closed-trades__total:first-child, .closed-trades__total:nth-child(2) { border-top: 0; }
  .performance-grid { grid-template-columns: repeat(2, 1fr); }
  .distribution-content { height: auto; flex-direction: column; }
  .distribution-legend { width: 100%; padding: 0 14px 12px; }
}
</style>
